import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { Redux } from '../..'
import { JobStatus } from '@/src/types/apps/operations'

interface OperationsInitialState {
  loading: boolean
  success: string | null
  error: string | null
  jobId?: number
  jobStatus?: JobStatus
  generateMenuJobId?: number
  generateMenuJobStatus?: JobStatus
  isSyncMenuDialogOpen: boolean
  isGenerateMenuDialogOpen: boolean
}

const initialState: OperationsInitialState = {
  loading: false,
  success: null,
  error: null,
  jobId: undefined,
  jobStatus: undefined,
  generateMenuJobId: undefined,
  generateMenuJobStatus: undefined,
  isSyncMenuDialogOpen: false,
  isGenerateMenuDialogOpen: false
}

export interface SyncMenuParams {
  menuId: number
}
export interface PollSyncMenuParams {
  jobId: number
}

export interface SyncMenuResponse {
  id: number
  queueUrl: string
  status: string
}

export interface PollSyncMenuResponse {
  id: number
  status: string
}

export interface GenerateMenuParams {
  channelId: number
  menuTemplateId?: number
  transformationTemplateId?: number
  groupId?: number
  storeId?: number
  shouldUpload?: boolean
}
export interface GenerateMenuResponse {
  id: number
  queueUrl: string
  status: string
}

export interface PollGenerateMenuParams {
  jobId: number
}

export interface PollGenerateMenuResponse {
  id: number
  status: string
}

export const syncMenu = createAsyncThunk<SyncMenuResponse, SyncMenuParams, Redux>(
  'operations/syncMenu',
  async (gygQueryOptions, { extra }) => {
    const { data: responseData } = await extra.httpClient.get('operations/sync-menu', { params: gygQueryOptions })

    return responseData.data
  }
)

export const pollSyncMenuJob = createAsyncThunk<PollSyncMenuResponse, PollSyncMenuParams, Redux>(
  'operations/pollSyncMenuJob',
  async ({ jobId }, { extra }) => {
    const { data: responseData } = await extra.httpClient.get(`operations/poll-sync-menu/${jobId}`)

    return responseData.data
  }
)
export const generateMenu = createAsyncThunk<GenerateMenuResponse, GenerateMenuParams, Redux>(
  'operations/generateMenu',
  async (payload, { extra }) => {
    const { data: responseData } = await extra.httpClient.post('operations/generate-menu', payload)

    return responseData.data
  }
)

export const pollGenerateMenuJob = createAsyncThunk<PollGenerateMenuResponse, PollGenerateMenuParams, Redux>(
  'operations/pollGenerateMenuJob',
  async ({ jobId }, { extra }) => {
    const { data: responseData } = await extra.httpClient.get(`operations/poll-generate-menu/${jobId}`)

    return responseData.data
  }
)

//* generate store menu
export const generateStoreMenu = createAsyncThunk<any, { storeId: number }, Redux>(
  'operations/generateStoreMenu',
  async ({ storeId }, { extra }) => {
    const response = await extra.httpClient(`menu-generate/store/${storeId}`)

    return response.data
  }
)

const gygOperationsSlice = createSlice({
  name: 'operations',
  initialState,
  reducers: {
    setSyncMenuDialogOpen: (state, action) => {
      state.isSyncMenuDialogOpen = action.payload
    },
    setGenerateMenuDialogOpen: (state, action) => {
      state.isGenerateMenuDialogOpen = action.payload
    }
  },
  extraReducers: builder => {
    builder
      .addCase(syncMenu.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(syncMenu.fulfilled, (state, action) => {
        state.loading = false
        state.success = 'status updated'
        state.jobId = action.payload.id
      })
      .addCase(syncMenu.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message ?? 'Failed to sync menu'
      })
      .addCase(pollSyncMenuJob.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(pollSyncMenuJob.fulfilled, (state, action) => {
        state.loading = false
        state.success = 'received status update'
        state.jobStatus = action.payload.status as JobStatus
      })
      .addCase(pollSyncMenuJob.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message ?? 'Failed to poll sync menu job'
      })
      .addCase(generateMenu.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(generateMenu.fulfilled, (state, action) => {
        state.loading = false
        state.success = 'status updated'
        state.generateMenuJobId = action.payload.id
      })
      .addCase(generateMenu.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message ?? 'Failed to sync menu'
      })
      .addCase(generateStoreMenu.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(generateStoreMenu.fulfilled, (state, action) => {
        state.loading = false
        state.success = 'status updated'
        state.generateMenuJobId = action.payload.data
      })
      .addCase(generateStoreMenu.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message ?? 'Failed to sync menu'
      })
      .addCase(pollGenerateMenuJob.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(pollGenerateMenuJob.fulfilled, (state, action) => {
        state.loading = false
        state.success = 'received status update'
        state.generateMenuJobStatus = action.payload.status as JobStatus
      })
      .addCase(pollGenerateMenuJob.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message ?? 'Failed to poll sync menu job'
      })
  }
})

export const { setSyncMenuDialogOpen, setGenerateMenuDialogOpen } = gygOperationsSlice.actions

export default gygOperationsSlice.reducer
